import "./serviceRequest.css";
import { useMemo, useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import { FormProvider, useForm } from 'react-hook-form';
import SelectField from "../form-fields/SelectField";
import DescriptionTextarea from "../form-fields/DescriptionTextarea";
import MultipleFileUploader from "../CustomUploader/MultipleFileUploader";
import {
  JPEG_PNG_EXTENSIONS,
  JPEG_PNG_MIME_TYPES,
} from "../CustomUploader/imageUploadConstants";
import Radio from "../form-fields/radio";
import apiHelper from "../../utils/apiHelper";
import {
    DocumentEntityTypesEnum, DocumentService, DocumentTypesEnum, DocumentVM, LookupService, LookupTypesEnum, PropertyService,
    ServiceRequestEntityTypesEnum, ServiceRequestService, UnitService
} from "@propertelligent/client-api";
import { UserLoginData } from "../../components/common/charts/Enums";
import { v4 as uuidv4 } from 'uuid';
import RadioGroup from "../form-fields/RadioGroup";

interface Option {
    Id: string | number;
    Name: string;
}

interface ServiceRequestValues {
    id: number;
    entityTypeId: string;
    serviceType: number;
    entityId: number;
    serviceSubType: number;
    description: string;
    requestDate: Date;
    serviceRequestStatus: number;
    documents_serviceRequestDocuments?: File[];
}

interface EditServiceRequestFormProps {
    isModalOpen: boolean;
    setModalOpen: (open: boolean) => void;
    fetchData?: (page: number) => void;
    response: any
}

const EditServiceRequestForm: React.FC<EditServiceRequestFormProps> = ({
    isModalOpen,
    setModalOpen,
    fetchData,
    response
}) => {
    const [resetCounter, setResetCounter] = useState(0);
    const [serviceTypes, setServiceTypes] = useState<Option[]>([]);
    const [serviceSubTypes, setServiceSubTypes] = useState<Option[]>([]);
    const [properties, setProperties] = useState<Option[]>([]);
    const [units, setUnits] = useState<Option[]>([]);
    const [isSubTypeHidden, setIsSubTypeHidden] = useState(false);
    const [isSubTypeDisabled, setIsSubTypeDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingImages, setIsFetchingImages] = useState(false);
    const [defaultImages, setDefaultImages] = useState<Array<{ src: string; name: string }>>([]);
    const [initialDocuments, setInitialDocuments] = useState<{ id: number; fileName: string }[]>([]);

    const memoizedServiceTypes = useMemo(() => serviceTypes, [serviceTypes]);
    const memoizedProperties = useMemo(() => properties, [properties]);
    const memoizedUnits = useMemo(() => units, [units]);
    const formId = `EditServiceRequestForm-${uuidv4()}`;

    const fetchInitialData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [serviceTypesData, propertiesData, unitsData] = await Promise.all([
                fetchLookupData(ServiceRequestService.getApiServiceRequestServiceTypes),
                fetchPropertyData(),
                fetchUnitData()
            ]);

            return {
                serviceTypes: serviceTypesData,
                properties: propertiesData,
                units: unitsData
            };
        } catch (error) {
            console.error("Error loading initial data:", error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchLookupData = useCallback(async (service: any) => {
        try {
            const response = await apiHelper(
                service,
                { showNotification: false }
            );
            return response?.data?.success?.map((val: any) => ({
                Id: val.id || val.Id || 0,
                Name: val.description || val.Name || val.name || ''
            })) || [];
        } catch (error) {
            console.error(`Error fetching lookup data for ${service}:`, error);
            return [];
        }
    }, []);

    const fetchPropertyData = useCallback(async () => {
        try {
            const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id) || '0');
            const response = await apiHelper(
                PropertyService.getApiPropertyOrganizationlookup,
                { showNotification: false },
                orgId
            );
            return response?.data?.success?.map((val: any) => ({
                Id: val.id || val.Id || 0,
                Name: val.description || val.Name || val.name || ''
            })) || [];
        } catch (error) {
            console.error("Error fetching properties:", error);
            return [];
        }
    }, []);

    const fetchUnitData = useCallback(async () => {
        try {
            const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id) || '0');
            const response = await apiHelper(
                UnitService.getApiUnitOrganizationlookup,
                { showNotification: false },
                orgId
            );
            return response?.data?.success?.map((val: any) => ({
                Id: val.id || val.Id || 0,
                Name: val.description || val.Name || val.name || ''
            })) || [];
        } catch (error) {
            console.error("Error fetching units:", error);
            return [];
        }
    }, []);

    const methods = useForm<ServiceRequestValues>({
        mode: "onChange",
        defaultValues: {
            id: null as any,
            entityTypeId: null as any,
            serviceType: null as any,
            entityId: null as any,
            serviceSubType: null as any,
            description: '',
            requestDate: null as any,
            serviceRequestStatus: null as any,
            documents_serviceRequestDocuments: []
        }
    });
    // RHF v7 only re-renders when formState fields are destructured (subscription).
    // Reading formState without that subscription left Save stale after a single field edit.
    const { formState: { isDirty, isValid, isSubmitting } } = methods;

    // Un-debounced core fetch, awaitable directly from the initial-load Promise.all below - awaiting the
    // debounced version doesn't actually wait for the deferred call, which raced methods.reset() and let a
    // stale/mismatched serviceSubType survive reconciliation (caught by ui-code-writer review). debounce is
    // only appropriate for the interactive handleServiceTypeChange path, not the one-shot initial load.
    const loadServiceSubTypes = useCallback(async (value: number) => {
        setIsSubTypeDisabled(true);
        try {
            const response = await apiHelper(
                ServiceRequestService.getApiServiceRequestServiceSubTypes,
                { showNotification: false },
                value
            );
            const data = response?.data?.success?.map((item: any) => ({
                Id: item.id || item.Id || 0,
                Name: item.name || item.Name || item.description || ''
            })) || [];
            setServiceSubTypes(data);
            return data as Option[];
        } catch (error) {
            console.error("Error fetching service sub types:", error);
            setServiceSubTypes([]);
            return [] as Option[];
        } finally {
            setIsSubTypeDisabled(false);
        }
    }, []);

    // Drops the current serviceSubType value if it doesn't belong to the freshly-fetched category
    // options - the single reconciliation point shared by both the debounced interactive path and the
    // awaited initial-load path below.
    const reconcileServiceSubType = useCallback((data: Option[]) => {
        const validIds = new Set(data.map((opt) => Number(opt.Id)));
        const currentValue = methods.getValues("serviceSubType");
        if (currentValue != null && !validIds.has(Number(currentValue))) {
            methods.setValue("serviceSubType", null as any, { shouldValidate: true });
        }
    }, [methods]);

    const fetchServiceSubTypes = useCallback(
        debounce((value: number) => {
            loadServiceSubTypes(value).then(reconcileServiceSubType);
        }, 300),
        [loadServiceSubTypes, reconcileServiceSubType]
    );

    // Service Type categories 6 ("no subtype applicable, field hidden") and 999 ("Other", field visible
    // but disabled/not-required) never have real Sub Type options - shared by the interactive change
    // handler and the initial-load effect below, so a persisted record stored under either category gets
    // the same hidden/disabled treatment on load that a user picking that category interactively gets.
    const getSubTypeVisibility = useCallback((value: number) => {
        switch (value) {
            case 6:
                return { hidden: true, disabled: false };
            case 999:
                return { hidden: false, disabled: true };
            default:
                return { hidden: false, disabled: false };
        }
    }, []);

    const handleServiceTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = Number(e.target.value);
        setServiceSubTypes([]);
        const { hidden, disabled } = getSubTypeVisibility(value);
        setIsSubTypeHidden(hidden);
        setIsSubTypeDisabled(disabled);

        if (hidden) {
            // The Sub Type field unmounts entirely for this category, so a plain setValue can't help -
            // RHF keeps the last-registered `required` rule alive after unmount (default
            // shouldUnregister: false), same gotcha already fixed for MultiFancyDropdown in
            // bug/contractor-subtype-save. unregister() fully deregisters value + rule + error so the
            // field starts clean if it remounts later for a different service type.
            methods.unregister("serviceSubType");
        } else {
            methods.setValue("serviceSubType", null as any, { shouldValidate: true });
            if (!disabled) {
                fetchServiceSubTypes(value);
            }
        }
    }, [fetchServiceSubTypes, getSubTypeVisibility, methods]);

    useEffect(() => {
        const initializeForm = async () => {
            if (!isModalOpen || !response) return;

            try {
                setIsFetchingImages(true);
                const serviceRequestId = response.success.id;
                const storedServiceType = response.success.serviceType;

                // Apply the same hidden/disabled treatment a user picking this category interactively
                // would get - a persisted "no subtype applicable" (6) or "Other" (999) record must not
                // render Sub Type as visible+required with no options, which would permanently block Save
                // once the form is dirtied (the field defaults to visible+required otherwise).
                const { hidden: storedHidden, disabled: storedDisabled } = getSubTypeVisibility(storedServiceType);
                setIsSubTypeHidden(storedHidden);
                setIsSubTypeDisabled(storedDisabled);

                // Only categories with real Sub Type options need the fetch - mirrors
                // handleServiceTypeChange's default-only fetchServiceSubTypes call. subTypesData is
                // genuinely awaited here (loadServiceSubTypes, not the debounced fetchServiceSubTypes) so
                // the reconciliation below is guaranteed to run after methods.reset().
                const shouldLoadSubTypes = !storedHidden && !storedDisabled;
                const [initialData, documentsResponse, subTypesData] = await Promise.all([
                    fetchInitialData(),
                    apiHelper(
                        DocumentService.getApiDocumentList,
                        { showNotification: false },
                        serviceRequestId,
                        DocumentEntityTypesEnum.ServiceRequest,
                        DocumentTypesEnum.BeforeImage
                    ),
                    shouldLoadSubTypes ? loadServiceSubTypes(storedServiceType) : Promise.resolve([] as Option[])
                ]);

                // Process form data
                if (initialData) {
                    setServiceTypes(initialData.serviceTypes);
                    setProperties(initialData.properties);
                    setUnits(initialData.units);
                }

                // Process documents
                if (documentsResponse?.data?.success && documentsResponse.data.success.length > 0) {
                    const documentArray: DocumentVM[] = documentsResponse.data.success;
                    setInitialDocuments(documentArray.map(doc => ({ id: doc.id!, fileName: doc.fileName! })));

                    const imagePromises = documentArray
                        .filter(doc => doc.id && doc.fileName?.toLowerCase().match(/\.(png|jpg|jpeg|gif)$/))
                        .map(async (doc) => {
                            try {
                                const base64Response = await apiHelper(
                                    DocumentService.getApiDocumentBase641, { showNotification: false }, doc.id!
                                );
                                if (base64Response?.data?.success) {
                                    return {
                                        src: `data:image/png;base64,${base64Response.data.success}`,
                                        name: doc.fileName || `document-${doc.id}`,
                                    };
                                }
                            } catch (error) {
                                console.error(`Error fetching base64 for document ${doc.id}:`, error);
                            }
                            return null;
                        });

                    const fetchedImages = (await Promise.all(imagePromises)).filter(img => img !== null) as Array<{ src: string; name: string }>;
                    setDefaultImages(fetchedImages);
                }

                methods.reset({
                    id: response.success.id,
                    entityTypeId: response.success.entityTypeId.toString(),
                    serviceType: response.success.serviceType,
                    entityId: response.success.entityId,
                    serviceSubType: response.success.serviceSubType,
                    description: response.success.description,
                    requestDate: new Date(response.success.requestDate),
                    serviceRequestStatus: response.success.serviceRequestStatus
                });

                if (storedHidden) {
                    // Mirrors handleServiceTypeChange's case 6: reset() just re-registered the field via
                    // its stored value, but it's not rendered for this category - unregister so its
                    // `required` rule can't outlive the unmount and get stuck (same gotcha documented on
                    // getSubTypeVisibility's `hidden` branch above).
                    methods.unregister("serviceSubType");
                } else if (shouldLoadSubTypes) {
                    // Reconcile now that reset() has actually run, using the options already fetched
                    // above - no timing dependency on the debounced fetchServiceSubTypes. Not needed for
                    // the disabled ("Other") category - that field isn't required, so a stale/sentinel
                    // value can't block Save, and onSubmit already coerces it to 0 on send.
                    reconcileServiceSubType(subTypesData);
                }
            } catch (error) {
                console.error("Error initializing form:", error);
            } finally {
                setIsFetchingImages(false);
            }
        };

        initializeForm();

        return () => {
            fetchServiceSubTypes.cancel();
        };
    }, [isModalOpen, response, methods, fetchInitialData, fetchServiceSubTypes, loadServiceSubTypes, reconcileServiceSubType, getSubTypeVisibility]);


    const onSubmit = async (data: ServiceRequestValues) => {
        try {
            setIsLoading(true);

            const requestData = {
                ...data,
                entityTypeId: Number(data.entityTypeId),
                serviceType: Number(data.serviceType),
                serviceSubType: Number(data.serviceType) === 999 ? 0 : (data.serviceSubType ? Number(data.serviceSubType) : null),
                entityId: Number(data.entityId),
                serviceRequestStatus: Number(data.serviceRequestStatus)
            };
            delete (requestData as any).documents_serviceRequestDocuments;

            const servReqRes = await apiHelper(
                ServiceRequestService.putApiServiceRequest,
                { successMessage: "Service updated successfully", showNotification: true },
                requestData
            );

            const serviceRequestId = servReqRes.data.success.id;

            // Document management
            const finalFiles = data.documents_serviceRequestDocuments || [];
            const finalFileNames = new Set(finalFiles.map(f => f.name));
            const initialFileNames = new Set(initialDocuments.map(d => d.fileName));

            const documentsToDelete = initialDocuments.filter(doc => !finalFileNames.has(doc.fileName));
            const filesToAdd = finalFiles.filter(file => !initialFileNames.has(file.name));

            const deletePromises = documentsToDelete.map(doc =>
                apiHelper(DocumentService.deleteApiDocument, { showNotification: false }, doc.id)
            );

            const addPromises = filesToAdd.map(file => {
                const apiDocObj = {
                    EntityTypeId: DocumentEntityTypesEnum.ServiceRequest,
                    EntityId: serviceRequestId,
                    DocumentTypeId: DocumentTypesEnum.BeforeImage,
                    IsMain: false,
                    FileName: file.name,
                    Document: file
                };
                return apiHelper(DocumentService.postApiDocument, { showNotification: false }, apiDocObj);
            });

            await Promise.all([...deletePromises, ...addPromises]);

            methods.reset();
            setResetCounter((prev) => prev + 1);
            setModalOpen(false);
            if (fetchData) fetchData(1);
        } catch (error) {
            console.error("Error submitting service request:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const entityType = methods.watch("entityTypeId");

    return (
        <RightSideModal
            isOpen={isModalOpen}
            onClose={() => setModalOpen(false)}
            title="Edit Service Request"
            buttonlabel={"Save"}
            formId={formId}
            maxWidth={550}
            isLoading={isSubmitting}
            isDirty={isDirty}
            isSubmitDisabled={isSubmitting || !isDirty || !isValid}
        >
            <Card className="no-bg">
                <FormProvider {...methods}>
                    <form id={formId} onSubmit={methods.handleSubmit(onSubmit)}>
                        <div className="row">
                            {memoizedProperties.length > 0 && (
                                <div className="col-md-12">
                                    <RadioGroup
                                        label="Create Service Request For"
                                        required
                                        requiredMessage="Please select one type"
                                    >
                                        <Radio
                                            name="entityTypeId"
                                            value={ServiceRequestEntityTypesEnum.Property.toString()}
                                            label="Property"
                                        />
                                        <Radio
                                            name="entityTypeId"
                                            value={ServiceRequestEntityTypesEnum.Unit.toString()}
                                            label="Unit"
                                        />
                                    </RadioGroup>
                                </div>
                            )}

                            {memoizedUnits.length > 0 && entityType === ServiceRequestEntityTypesEnum.Unit.toString() && (
                                <div className="col-md-12">
                                    <SelectField
                                        placeholderText="Select a Unit"
                                        name="entityId"
                                        label="Unit"
                                        required
                                        requiredMessage="Please select a Unit"
                                        options={memoizedUnits}
                                        disabled={isLoading}
                                        isSearchable={true}
                                    />
                                </div>
                            )}

                            {entityType === ServiceRequestEntityTypesEnum.Property.toString() && (
                                <div className="col-md-12">
                                    <SelectField
                                        placeholderText="Select a Property"
                                        name="entityId"
                                        label="Property"
                                        required
                                        requiredMessage="Please select a property"
                                        options={memoizedProperties}
                                        disabled={isLoading}
                                        isSearchable={true}
                                    />
                                </div>
                            )}

                            <div className="col-md-6">
                                <SelectField
                                    name="serviceType"
                                    label="Service Type"
                                    required
                                    requiredMessage="Please select a service type"
                                    options={memoizedServiceTypes}
                                    onChange={handleServiceTypeChange}
                                    disabled={isLoading}
                                />
                            </div>

                            {!isSubTypeHidden && (
                                <div className="col-md-6">
                                    <SelectField
                                        name="serviceSubType"
                                        label="Sub Type"
                                        required={!isSubTypeDisabled}
                                        requiredMessage="Please select a sub type"
                                        options={serviceSubTypes}
                                        disabled={isSubTypeDisabled || isLoading}
                                    />
                                </div>
                            )}

                            <div className="col-md-12">
                                <DescriptionTextarea
                                    name="description"
                                    requiredMessage="Description is required"
                                    rows={3}
                                    required
                                    disabled={isLoading}
                                    placeholder="Enter Description"
                                    validation={{
                                        maxLength: { value: 250, message: "Description cannot exceed 250 characters" }
                                    }}
                                />
                            </div>

                            <div className="col-md-12">
                                {isFetchingImages ? (
                                    <div className="form-group">
                                        <label className="fw-600">Upload Documents</label>
                                        <div style={{ padding: '20px', textAlign: 'center', border: '2px dashed #ccc', borderRadius: '8px' }}>
                                            Loading images...
                                        </div>
                                    </div>
                                ) : (
                                    <MultipleFileUploader
                                        name="documents_serviceRequestDocuments"
                                        uploaderId="editServiceRequestDocuments"
                                        label="Upload Documents"
                                        disabled={isLoading}
                                        allowedFormats={[...JPEG_PNG_MIME_TYPES]}
                                        allowedExtensions={[...JPEG_PNG_EXTENSIONS]}
                                        maxSizeMB={1}
                                        maxFiles={5}
                                        resetTrigger={resetCounter}
                                        defaultImages={defaultImages}
                                        uploadertext="Upload Files"
                                        uploadertypeslabel="Max. size 1MB. JPEG, PNG (Max 5 files)"
                                        convertImagesToPng={false}
                                    />
                                )}
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </Card>
        </RightSideModal>
    );
};

export default EditServiceRequestForm;